import dayjs from 'dayjs';
import JSON5 from 'json5';
import { get } from 'lodash-es';
import { h } from 'vue';
import type { TableRowSelection, TableSetting } from '@mts/components/Table';
import type { Nullable } from '@mts-fe/types';
import { DAY_FORMAT, deepMerge, getFormConfig, omit } from '@mts-fe/utils';
import MIcon from '@mts/components/Icon/src/MIcon.vue';
import { t } from '@mts/hooks/web/useI18n';
import { IN_DEV, PADMode } from '@mts/utils/env';
import type { UserInfo } from '@/api/ims-auth/model';
import { getGlobalStore, getUserStore } from '@/store';
import type { AreaPane, TemplateAction } from '@/views/lowcode/CFConfig';
import type {
  ActionBtnItem,
  BaseFormProps,
  BaseFormSchema,
  BaseModal,
  DictOptionParams,
  FormTabPane,
  PageCode,
  PageModal,
  RenderTableColumn,
  RenderTableProps,
} from '..';
import { DICT_OPTION_API } from '../api';
import { COMPARATOR, getComparator, getDefComparator } from '..';
import { renderCompMap, tableCellCompOptions, useMapValueComps } from './componentMap';
import { parseFormSchemas } from './parser';

/**
 * 低代码平台渲染工具函数
 * 提供表单、表格、按钮等组件的配置转换和数据处理功能
 */

/**
 * 获取字典选项API配置
 * @param params 字典选项参数
 * @returns API配置对象或undefined
 */
const getOptionApiProps = ({ modelFieldId, dataTypeCode, search, filter }: DictOptionParams) =>
  modelFieldId || dataTypeCode
    ? {
        api: DICT_OPTION_API,
        params: { modelFieldId, dataTypeCode, search: { ...search }, filter: { ...filter } },
      }
    : void 0;

/**
 * 合并JSON Schema配置
 * 将字符串形式的JSON Schema与基础Schema合并，支持回调处理
 * @param jsonSchema JSON Schema字符串
 * @param schema 基础Schema对象
 * @param callback 合并后的回调处理函数
 * @returns 合并后的Schema对象
 */
export const mergeJsonSchema = <T extends Recordable>(
  jsonSchema = '',
  schema: T,
  callback?: (data: T, parsedSchema: Recordable) => T,
) => {
  if (!jsonSchema) {
    return callback ? callback(schema, {}) : schema;
  }

  try {
    const parsedSchema = JSON.parse(jsonSchema) as Recordable;
    const mergedSchema = deepMerge(omit(schema, ignoreKeys) as T, parsedSchema);
    return callback ? callback(mergedSchema, parsedSchema) : mergedSchema;
  } catch (error) {
    console.error('[SCHEMA_CONVERTER]', jsonSchema, error);
    return schema;
  }
};

// 优化：缓存全局用户信息，避免重复获取
let cachedUserInfo: Nullable<UserInfo> = null;
const getCachedUserInfo = () => {
  if (!cachedUserInfo) {
    cachedUserInfo = getUserStore().getUserInfo;
  }
  return cachedUserInfo;
};

const DATE = { TODAY: dayjs() };
const RANGE_DATE = {
  NEAREST_30_DAYS: [dayjs().startOf('day').subtract(29, 'day'), dayjs().endOf('day')],
  MONTH: [dayjs().startOf('month'), dayjs().endOf('month')],
  TODAY: [dayjs().startOf('day'), dayjs().endOf('day')],
};
/**
 * 全局变量配置
 * 提供当前用户、系统配置、日期等常用变量
 */
const getGlobalValue = () => {
  const curUserInfo = getCachedUserInfo();
  return {
    CUR_USER: {
      DEPT: curUserInfo.groupId || '',
      ID: curUserInfo.id,
      NO: curUserInfo.no,
    },
    SYS: getGlobalStore().getConfig,
    DATE,
    RANGE_DATE,
  };
};

/**
 * 字符串转JSON对象
 * 使用JSON5解析，支持未加引号的属性名
 * @param val 字符串值
 * @returns 解析后的JSON对象或原字符串
 */
const formatToJson = (val: string) => {
  try {
    // 给未加引号的字符串加引号（排除数字）
    return JSON5.parse(val.replace(/([,\\[]\s*)([a-zA-Z_][a-zA-Z0-9_]*)(\s*[,\]])/g, '$1"$2"$3'));
  } catch (error) {
    console.warn(val, error);
    return val;
  }
};

const simpleTextReg = /^\w/;
/**
 * 获取字段默认值
 * 支持函数执行、全局变量引用、JSON解析等多种格式
 * @param val 默认值字符串
 * @returns 处理后的默认值
 */
const getDefaultValue = (val?: string) => {
  if (!val) return val;

  if (val.startsWith('NUM|')) return +val.replace('NUM|', '');

  if (val.startsWith('RUN_AS_FN|')) {
    const curUserInfo = getCachedUserInfo();
    return new Function('dayjs,curUserInfo', val.replace('RUN_AS_FN|', ''))(dayjs, curUserInfo);
  }

  const globalValue = getGlobalValue();
  if (val.split('|').length && Object.keys(globalValue).some((i) => val.startsWith(i))) {
    return get(globalValue, val.split('|'));
  }

  // 普通字符串或JSON解析
  return simpleTextReg.test(val) ? val : formatToJson(val);
};

/**
 * 计算列数对应的span值（基于24栅格系统）
 * @param colNum 列数
 * @param defSpan 默认span值
 * @returns 计算后的span值
 */
const getColNumToSpan = (colNum?: number, defSpan = 4) =>
  colNum ? Math.ceil(24 / +colNum) : defSpan;

/**
 * 计算字段列数
 * @param baseColNum 基础列数
 * @param itemMultiple 字段倍数
 * @returns 计算后的列数
 */
const getFieldColNum = (baseColNum: number, itemMultiple = 1) => {
  return baseColNum / (itemMultiple > baseColNum ? baseColNum : itemMultiple);
};

/**
 * 字段占比转span值
 * @param baseColNum 基础列数
 * @param itemMultiple 字段倍数
 * @returns 对应的span值
 */
const getFieldColSpan = (baseColNum: number, itemMultiple = 1) =>
  getColNumToSpan(getFieldColNum(baseColNum, itemMultiple));

/**
 * 获取表单列配置
 * @param baseColNum 基础列数
 * @param actionSpan 操作列span倍数
 * @returns 表单列配置对象
 */
const getFormColProps = (baseColNum: number, actionSpan = 1) => ({
  baseColProps: { span: getFieldColSpan(baseColNum) },
  actionColOptions: { span: getFieldColSpan(baseColNum, actionSpan) },
});

/**
 * JS字符串转函数
 * @param params 参数字符串
 * @param functionBody 函数体字符串
 * @returns 生成函数
 */
const createFunction = (params: string, functionBody = '') =>
  new Function(`return function (${params}) { ${functionBody} }`);

/* 基础数据函数 */
const runBaseRecordFn = (functionBody = '') => createFunction('record', functionBody)();

/* 比较函数 */
const runCompareFn = (functionBody = '') => createFunction('a,b', functionBody)();

/* 表格数据处理函数 */
const runTableDataFn = (functionBody = '') => createFunction('data,res', functionBody)();

/* 表格列渲染函数 */
const runTableColFn = (functionBody = '') =>
  createFunction('{text,record,column,index,route,sysConfig}', functionBody)();

const runTableCellFn = (functionBody = '') => createFunction('record,index,column', functionBody)();
const runTableTotalFn = (functionBody = '') =>
  createFunction('dataSource,column,context', functionBody)();

/* 表格列显示控制函数 */
const runTableColShowFn = (functionBody = '') =>
  createFunction('column,{route,sysConfig}', functionBody)();

/* 表单字段处理函数 */
const runFormSchemaFn = (functionBody = '') =>
  createFunction('{schema,values,model,field,route,sysConfig,hasPermission}', functionBody)();

/**
 * 表单功能属性列表
 * 这些属性支持字符串形式的函数定义
 */
const FUNCTIONAL_FORM_PROPS = [
  'show',
  'ifShow',
  'dynamicDisabled',
  'dynamicReadonly',
  'dynamicRules',
  'valueFormat',
  'helpMessage',
  'required',
  'suffix',
  'prefix',
];

/**
 * 函数化表单Schema
 * 将字符串形式的函数定义转换为实际函数
 * @param schema 表单Schema
 * @param formPropKeys 需要函数化的属性列表
 * @returns 函数化后的Schema
 */
const functionalizeFormSchema = (schema: BaseFormSchema, formPropKeys = FUNCTIONAL_FORM_PROPS) => {
  formPropKeys.forEach((key) => {
    if (typeof schema[key] === 'string' && !['true', 'false'].includes(schema[key])) {
      schema[key] = runFormSchemaFn(schema[key]);
    }
  });
  return schema;
};

const getBaseFilter = (i: Recordable) => ({
  hiddenAdd: i.hiddenAdd,
  hiddenEdit: i.hiddenEdit,
  hiddenDetail: i.hiddenDetail,
  disabledAdd: i.disabledAdd,
  disabledEdit: i.disabledEdit,
  disabledDetail: i.disabledDetail,
});

const TREE_MODE_PROPS = {
  DEPT: { api: '/ims-auth/sysDept/list-tree-all', labelField: 'name', valueField: 'id' },
};

/** 解析表单字段 */
const getFormSchemas = (fields: Recordable[] = [], baseColNum: number, area: AreaPane) => {
  const checkHiddenMode = getGlobalStore().getCheckHidden;
  return fields
    .filter((i) => i.enable && !(checkHiddenMode && i.checkHidden))
    .map((i) => {
      return mergeJsonSchema(
        i.jsonSchema,
        {
          field: i.field,
          label: i.columnTitle,
          modelFieldId: i.modelFieldId,
          dataTypeCode: i.dataTypeCode,
          colProps:
            area.layout !== 'inline' ? { span: getFieldColSpan(baseColNum, i.columnSpan) } : void 0,
          component: getFieldComponent(i.component, 'Input'),
          componentProps: getComponentProps(i),
          defaultValue: getDefaultValue(i.defaultValue),
          hideInMore: area.filterModalEnable && !i.quickerFieldEnable,
          triggerSearch: i.triggerSearch,
          showIn: i.showIn,
          show: !i.invisible,
          required: i.required,
          ...getBaseFilter(i),
          auth: i.auth,
        } as BaseFormSchema,
        (schema) => {
          if (schema.componentProps.treeMode) {
            const { treeMode, ...restProps } = schema.componentProps;
            schema.componentProps = { ...TREE_MODE_PROPS[treeMode], ...restProps };
          }
          if (schema.componentProps.showScan) {
            const { showScan, ...restProps } = schema.componentProps;
            schema.itemProps = { 'data-scan': schema.field, ...schema.itemProps } as any;
            schema.componentProps = {
              prefix: h(MIcon, { icon: 'icon-g-scan' }),
              placeholder: t('g.26050003'),
              ...restProps,
            };
          }
          return functionalizeFormSchema(schema);
        },
      );
    });
};

const getFormConfigByArea = (area: AreaPane): BaseFormProps => ({
  _ID: area.areaCode,
  layout: area.layout,
  labelWidth: area.width,
  schemas: getFormSchemas(area.fields, area.columnSpan!, area),
  ...(area.layout !== 'inline' ? getFormColProps(area.columnSpan!, area.actionColumnWidth) : {}),
});
/** 将基础的类表单区域转成配置 */
export const getBaseFormConfig = (area: AreaPane, pageCode: PageCode) =>
  mergeJsonSchema(area.jsonSchema, {
    ...getFormConfigByArea(area),
    name: `${pageCode}.${area.areaCode || area.id}_FORM`,
    // TODO 迁移
    api: area.api,
    modelId: area.modelId,
    flatParam: area.flatParam,
  } as BaseFormProps);

/** 将基础的类查询区域转成配置 */
export const getBaseSearchConfig = (area: AreaPane, pageCode: PageCode) => {
  const { schemas, ...restConfig } = getFormConfigByArea(area);
  return mergeJsonSchema(area.jsonSchema, {
    ...restConfig,
    name: `${pageCode}.${area.areaCode || area.id}_SEARCH`,
    // showPivot: false,
    compact: true,
    showActionButtonGroup: true,
    autoSubmitOnEnter: true,
    showMoreButton: area.filterModalEnable,
    moreButtonOptions: { text: area.filterModalName },
    // 透传区域列数配置，供 PopoverSearch 计算每行等宽列数
    columnSpan: area.columnSpan,
    schemas: schemas?.map((item) => ({
      ...item,
      comparator: getComparator(
        item.comparator,
        item.component === 'ApiSelect' && item.componentProps?.mode === 'multiple'
          ? COMPARATOR.IN
          : getDefComparator(item.component),
      ),
    })),
  } as BaseFormProps);
};
const cardFunctionKeys = [
  'getCardTitle',
  'getSubTitle',
  'getCardClass',
  'getCardStatus',
  'ifArrowShow',
  'ifDeleteShow',
];
const dataListFunctionKeys = ['rowClass', 'labelClass', 'valueClass'];
/** 将基础的类表格区域转成配置 */
export const getBaseTableConfig = (area: AreaPane, pageCode: PageCode) =>
  mergeJsonSchema(
    area.jsonSchema,
    {
      _ID: area.areaCode,
      api: area.api,
      modelId: area.modelId,
      tableId: `${pageCode}.${area.areaCode || area.id}_${area.areaType.replace(/BASE_|DATA_/g, '')}`,
      showIndexColumn: false,
      rowSelection: area.selectMode
        ? ({ type: area.selectMode, fixed: true } as TableRowSelection)
        : void 0,
      columns: getTableColumns(area.fields),
      flatParam: area.flatParam,
      showBatchBar: area.batchBarEnable,
      batchBar: area.batchBarEnable ? getTableBatchBar(area.fields) : void 0,
      pagination: area.hidePagination ? false : void 0,
      showTableSetting: area.setting,
      tableSetting: getTableSetting(area.setting),
      actionColumn: getTableActionColumn(area),
      actions: getTableActions(area.actions, area.modelId),
      gridColumns: area.columnSpan,
      dataListProps: { horizontal: area.layout === 'horizontal' },
      cardActionKey: area.cardActionKey,
      arrowActionKey: area.arrowActionKey,
      deleteActionKey: area.deleteActionKey,
    } as RenderTableProps,
    (schema) => {
      if (typeof schema.rowSelection?.getCheckboxProps === 'string') {
        schema.rowSelection.getCheckboxProps = runBaseRecordFn(
          schema.rowSelection.getCheckboxProps,
        );
      }
      if (typeof schema.afterFetch === 'string') {
        schema.afterFetch = runTableDataFn(schema.afterFetch);
      }
      if (typeof schema.customRow === 'string') {
        schema.customRow = runTableCellFn(schema.customRow);
      }
      cardFunctionKeys.forEach((key) => {
        if (typeof schema[key] === 'string') {
          schema[key] = runBaseRecordFn(schema[key]);
        }
      });
      dataListFunctionKeys.forEach((key) => {
        if (schema.dataListProps) {
          const keyValue = schema.dataListProps[key];
          if (typeof keyValue === 'string' && keyValue.startsWith('return ')) {
            schema.dataListProps[key] = runTableColFn(keyValue);
          }
        }
      });
      return schema;
    },
  );
const getTableColumns = (fields: Recordable[] = []) => {
  const checkHiddenMode = getGlobalStore().getCheckHidden;
  return fields
    .filter((i) => i.enable && !(checkHiddenMode && i.checkHidden))
    .map((i) => {
      return mergeJsonSchema(
        i.jsonSchema,
        {
          dataIndex: i.field?.includes('.') ? i.field.split('.') : i.field,
          title: i.columnTitle,
          width: i.width || 120,
          align: i.align,
          ellipsis: false,
          resizable: true,
          defaultHidden: false,
          showIn: i.showIn,
          sorter: i.sorter,
          filter: i.filter ? getOptionApiProps(i) : void 0,
          required: i.required,
          showTotal: i.total,
          ...getBaseFilter(i),
          ...getTableFieldComp(i),
          auth: i.auth,
        } as RenderTableColumn,
        (schema) => {
          if (schema.render?.api === 'DICT_OPTION_API') {
            schema.render.api = DICT_OPTION_API;
          }
          if (typeof schema.render?.getStatus === 'string') {
            schema.render.getStatus = runTableColFn(schema.render.getStatus);
          }
          if (typeof schema.ifShow === 'string') {
            schema.ifShow = runTableColShowFn(schema.ifShow);
          }
          if (typeof schema.ifEditShow === 'string') {
            schema.ifEditShow = runTableColFn(schema.ifEditShow);
          }
          if (typeof schema.editDynamicDisabled === 'string') {
            schema.editDynamicDisabled = runTableColFn(schema.editDynamicDisabled);
          }
          dataListFunctionKeys.forEach((key) => {
            const keyValue = schema[key];
            if (typeof keyValue === 'string' && keyValue.startsWith('return ')) {
              schema[key] = runTableColFn(keyValue);
            }
          });
          if (typeof schema.customCell === 'string') {
            schema.customCell = runTableCellFn(schema.customCell);
          }

          if (schema.defaultFilteredValue) {
            schema.filters = schema.filters ?? [];
            schema.filtered = schema.filtered ?? true;
            schema.filterResetToDefaultFilteredValue =
              schema.filterResetToDefaultFilteredValue ?? true;
          }
          if (typeof schema.sorter === 'string') {
            schema.sorter = runCompareFn(schema.sorter);
          }
          if (typeof schema.customTotal === 'string') {
            schema.customTotal = runTableTotalFn(schema.customTotal);
          }
          return schema;
        },
      );
    });
};
const getTableFieldComp = ({ component, ...rest }: Recordable) => {
  if (!component && rest.showIn) {
    component = { statusTag: 'RenderTag', statusText: 'RenderTextTag' }[rest.showIn];
  }

  if (!component) {
    return { editComponentProps: getOptionApiProps(rest) };
  }

  const renderComp = tableCellCompOptions.find(({ value }) => component === value);
  if (renderComp) {
    const apiProps = useMapValueComps.includes(component) ? getOptionApiProps(rest) : {};
    const propsMap = {
      RenderLink: { href: rest.url },
      RenderTextTag: { textMode: true },
    };
    const type = { RenderTextTag: 'RenderTag' }[component] || component;
    return { render: deepMerge({ ...propsMap[component], ...rest.render, type }, apiProps) };
  }
  return {
    editComponent: getFieldComponent(component),
    editComponentProps: getComponentProps({ component, ...rest }),
  };
};
const inputReadOnly = PADMode;
const todayTimeRange = [dayjs('00:00:00', DAY_FORMAT.TS), dayjs('23:59:59', DAY_FORMAT.TS)];
const getFieldComponent = (component: string, defComponent?: string) =>
  renderCompMap[component] || component || defComponent;
const getComponentProps = ({ component, linkAreaId, ...rest }: DictOptionParams & Recordable) => {
  const apiProps = getOptionApiProps(rest);
  const propsMap = {
    ApiSelect: { ...apiProps, dropdownMatchSelectWidth: false, popupClassName: 'max-w-50vw' },
    ApiSelectSearch: {
      showSearch: true,
      dropdownMatchSelectWidth: false,
      popupClassName: 'max-w-50vw',
      ...apiProps,
      params: {
        ...apiProps?.params,
        search: { selectType: rest.field, ...apiProps?.params.search },
      },
      apiSearch: {},
    },
    ApiInputAddSelect: { ...apiProps, inputAdd: true },
    ApiMultipleSelect: { ...apiProps, mode: 'multiple' },
    ApiSelectAdd: { ...apiProps, modalId: linkAreaId },
    InputCode: { modalId: linkAreaId },
    ModalTableSelect: { modalId: linkAreaId },
    ReadonlyText: { ...apiProps },
    TooltipText: { ...apiProps, showTips: true, truncate: true },
    Textarea: { autoSize: { maxRows: 5 } },
    ApiTreeSelect: { ...apiProps },
    ApiCascader: {
      api: apiProps?.api,
      initFetchParams: apiProps?.params,
      expandTrigger: 'hover',
      showSearch: true,
      loadData: null,
      isLeaf: (item) => !item.children?.length,
    },
    ApiRadioGroup: apiProps,
    ApiRadioButtonGroup: { ...apiProps, isBtn: true },
    ApiSegmented: { ...apiProps },
    ApiSegmentedSearch: { ...apiProps, block: true, prependAll: true, useFirstValue: true },
    Switch: { unCheckedValue: 0, checkedValue: 1 },
    TimePicker: { format: DAY_FORMAT.T, valueFormat: DAY_FORMAT.T, showNow: true, inputReadOnly },
    DatePicker: { picker: 'date', format: DAY_FORMAT.D, valueFormat: DAY_FORMAT.D, inputReadOnly },
    DateTimePicker: {
      picker: 'date',
      format: DAY_FORMAT.DT,
      valueFormat: DAY_FORMAT.DTS,
      showTime: true,
      showNow: true,
      inputReadOnly,
    },
    MonthPicker: {
      picker: 'month',
      format: DAY_FORMAT.M,
      valueFormat: DAY_FORMAT.M,
      inputReadOnly,
    },
    YearPicker: { picker: 'year', format: DAY_FORMAT.Y, valueFormat: DAY_FORMAT.Y, inputReadOnly },
    RangePicker: { format: DAY_FORMAT.D, valueFormat: DAY_FORMAT.D, inputReadOnly },
    QuickRangePicker: {
      format: DAY_FORMAT.D,
      valueFormat: DAY_FORMAT.D,
      showPresets: true,
      showQuick: true,
      inputReadOnly,
    },
    RangeTimePicker: {
      format: DAY_FORMAT.DT,
      valueFormat: DAY_FORMAT.DTS,
      showTime: { hideDisabledOptions: true, defaultValue: todayTimeRange },
      inputReadOnly,
    },
    DateInput: { type: 'date', valueFormat: DAY_FORMAT.D, min: '2000-01-01', max: '2099-12-31' },
    TimeInput: { type: 'time', valueFormat: DAY_FORMAT.TS },
  };
  return propsMap[component] || {};
};
const getTableBatchBar = (fields: Recordable[] = []) =>
  getFormConfig({
    compact: true,
    schemas: parseFormSchemas(
      fields
        .filter((i) => i.enable && i.batchEnable)
        .map((i) => {
          const schema = mergeJsonSchema(i.jsonSchema, {
            field: i.field,
            label: i.columnTitle,
            auth: i.auth,
            ...getTableFieldComp(i),
          }) as Recordable;
          if (!schema.component && schema.editComponent) {
            schema.component = schema.editComponent;
          }
          if (!schema.componentProps && schema.editComponentProps) {
            schema.componentProps = schema.editComponentProps;
          }
          if (typeof schema.render === 'object') {
            delete schema.render;
          }
          return schema as BaseFormSchema;
        })
        .filter((i: BaseFormSchema) => i.component),
    ),
  });
const getTableActions = (actions: TemplateAction[] = [], modelId?: string) =>
  getBaseBtnsConfig(
    actions,
    (schema) => ({ ...getBaseFilter(schema), modelId }),
    (schema) => {
      if (['ROW_DELETE', 'RECORD_DELETE'].includes(schema.eventType)) {
        const title = schema.popConfirm?.title || 'common.deleteConfirm';
        Object.assign(schema, {
          popConfirm: { placement: 'topRight', ...schema.popConfirm, title },
        });
      }
      return schema;
    },
  );
/** 表操作列 */
const getTableActionColumn = (area: AreaPane) => {
  if (area.actionColumnEnable) {
    return { dataIndex: 'ACTION', width: area.actionColumnWidth };
  }
};
/** 表设置 */
const getTableSetting = (
  enabled?: boolean,
  mode: TableSetting['fullScreen'] = true,
): TableSetting => ({
  redo: enabled,
  size: false,
  setting: enabled,
  settingCache: !IN_DEV,
  fullScreen: enabled && mode,
});

/** 将基础的类按钮区域转成配置 */
export const getBaseBtnsConfig = (
  actions: TemplateAction[] = [],
  itemMerge: (schema: TemplateAction) => Recordable = () => ({}),
  itemCallback: (schema: ActionBtnItem) => ActionBtnItem = (schema) => schema,
) => {
  const checkHiddenMode = getGlobalStore().getCheckHidden;
  return actions
    .filter((i) => i.enable && !(checkHiddenMode && i.checkHidden))
    .map((i) => {
      return mergeJsonSchema(
        i.jsonSchema,
        {
          ...omit(i, btnIgnoreKeys),
          ...itemMerge(i),
          eventType: i.feature,
          label: i.btnName,
          color: i.btnColor,
          [i.linkAreaId && Number.isNaN(+i.linkAreaId) ? 'modalCode' : 'modalId']: i.linkAreaId,
          eventApi: i.api,
          routeUrl: i.url,
          taskCode: i.asyncTaskCode,
        } as ActionBtnItem,
        (schema) => {
          if (typeof schema.ifBtnShow === 'string') {
            schema.ifBtnShow = runTableColFn(schema.ifBtnShow);
          }
          if (typeof schema.dynamicDisabled === 'string') {
            schema.dynamicDisabled = runTableColFn(schema.dynamicDisabled);
          }
          return itemCallback(schema);
        },
      );
    });
};
/** 解析顶部按钮区 */
export const getHeaderBtns = (areas: AreaPane[], modelId?: string) => {
  const area = areas.filter((area) => area.enable && area.areaType === 'HEADER_BTNS')[0];
  return area ? getBaseBtnsConfig(area.buttons, () => ({ modelId })) : [];
};

/** 解析搜索区 */
export const getDataSearch = (areas: AreaPane[], pageCode: PageCode) => {
  const area = areas.filter((area) => area.enable && area.areaType === 'DATA_SEARCH')[0];
  return area ? getBaseSearchConfig(area, pageCode) : area;
};

export const getDataTable = (areas: AreaPane[], pageCode: PageCode) => {
  const area = areas.filter((area) => area.enable && area.areaType === 'DATA_TABLE')[0];
  return area ? getBaseTableConfig(area, pageCode) : area;
};

export const getBaseModals = (areas: AreaPane[]) =>
  areas
    .filter((area) => area.enable && area.areaType === 'BASE_MODAL')
    .map((area) => {
      return mergeJsonSchema(area.jsonSchema, {
        _ID: area.areaCode,
        modalId: area.id,
        title: area.areaName,
        size: area.modalSize,
        insideAreaCode: area.insideAreaCode,
        eventApi: area.api,
        routeUrl: area.url,
      } as BaseModal);
    });

/** 解析页面弹窗 */
export const getPageModals = (areas: AreaPane[], pageCode: PageCode) => {
  return areas
    .filter((area) => area.enable && area.areaType === 'PAGE_MODAL' && area.linkPageAreas?.length)
    .map((area) => {
      const modalPageCode = pageCode + '.' + area.id;
      const modalHandler = { eventApi: area.api, routeUrl: area.url, flatParam: area.flatParam };
      const mainFormArea = area.linkPageAreas!.filter(
        (area) => area.enable && area.areaType === 'PAGE_FORM',
      )[0];
      if (mainFormArea) {
        return mergeJsonSchema(area.jsonSchema, {
          _ID: area.areaCode,
          modalId: area.id,
          modal: { title: area.areaName, size: area.modalSize },
          modalHandler,

          renderMode: 'MODAL_FORM',
          api: mainFormArea.api,
          modelId: mainFormArea.modelId,
          mainForm: getBaseFormConfig(mainFormArea, pageCode),
          childTabs: getPageItemTabs(area.linkPageAreas!, modalPageCode),
        } as PageModal);
      }

      const tableArea = area.linkPageAreas!.filter(
        (area) => area.enable && area.areaType === 'DATA_TABLE',
      )[0];
      if (tableArea) {
        return mergeJsonSchema(area.jsonSchema, {
          _ID: area.areaCode,
          modalId: area.id,
          modal: { title: area.areaName, size: area.modalSize },
          modalHandler,

          renderMode: 'MODAL_TABLE',
          api: tableArea.api,
          modelId: tableArea.modelId,
          headerBtns: getHeaderBtns(area.linkPageAreas!, tableArea.modelId),
          search: getDataSearch(area.linkPageAreas!, modalPageCode),
          table: getDataTable(area.linkPageAreas!, modalPageCode),
          modals: getPageModals(area.linkPageAreas!, modalPageCode),
        } as PageModal);
      }
      console.error('弹窗区域异常', area);
      return { modal: { title: area.areaName + '【渲染异常】' } } as PageModal;
    });
};

export const getPageItemTabs = (areas: AreaPane[], pageCode: PageCode) => {
  return areas
    .filter((area) => area.enable && area.areaType === 'TAB_TABLE')
    .map((area) => {
      return mergeJsonSchema(
        area.jsonSchema,
        {
          tabKey: area.id.toString(),
          tabName: area.areaName,
          relationKey: area.relationKey || area.subTabKey,
          inset: true,
          showIndexColumn: false,
          tableId: `${pageCode}.${area.id}_TABLE`,
          columns: getTableColumns(area.fields),
          showBatchBar: area.batchBarEnable,
          batchBar: area.batchBarEnable ? getTableBatchBar(area.fields) : void 0,
          pagination: area.hidePagination ? false : void 0,
          showTableSetting: area.setting,
          tableSetting: getTableSetting(area.setting, 'APP'),
          actionColumn: getTableActionColumn(area),
          actions: getTableActions(area.actions),
        } as FormTabPane,
        (schema) => {
          if (typeof schema.rowSelection?.getCheckboxProps === 'string') {
            schema.rowSelection.getCheckboxProps = runBaseRecordFn(
              schema.rowSelection.getCheckboxProps,
            );
          }
          return schema;
        },
      );
    });
};

const ignoreKeys = [
  'jsonSchema',
  'deleted',
  'createBy',
  'createTime',
  'updateBy',
  'updateTime',
  'tenantId',
  'version',
  'parentId',
  'status',
  'dataStatus',
  'checkHidden',
  'standbyEnable',
  'canUpdateRequired',
  'createByDeptId',
  'createByFactoryId',
];

const btnIgnoreKeys = [
  ...ignoreKeys,
  'enable',
  'id',
  'columnTitle',
  'orderIndex',
  'invisible',
  'feature',
  'btnName',
  'btnColor',
  'linkAreaId',
  'asyncTaskCode',
  'api',
  'url',
];
